import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useCustomerAuth } from "../lib/CustomerAuthContext";
import { toast } from "sonner";
import { LogOut, Package, ArrowRight, ShieldCheck } from "lucide-react";

const fmtErr = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
};

const AccountPage = () => {
  const { customer, loading, login, register, logout } = useCustomerAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | register
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  useEffect(() => {
    if (!customer) return;
    setOrdersLoading(true);
    api.get("/customer/orders", { withCredentials: true })
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
  }, [customer]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || (mode === "register" && !form.name)) {
      toast.error("Fill all required fields"); return;
    }
    if (mode === "register" && form.password.length < 8) {
      toast.error("Password must be at least 8 characters"); return;
    }
    setSubmitting(true);
    try {
      if (mode === "register") {
        await register(form);
        toast.success(`Welcome, ${form.name.split(" ")[0]}`);
      } else {
        const u = await login({ email: form.email, password: form.password });
        toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      }
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <NavBar />
        <div className="max-w-4xl mx-auto px-4 py-20 font-mono-tech text-xs uppercase text-zinc-500">// Loading…</div>
      </div>
    );
  }

  // Signed-out state — email/password form
  if (!customer) {
    const isRegister = mode === "register";
    return (
      <div className="min-h-screen bg-zinc-950">
        <NavBar />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-500 mb-3 uppercase flex items-center">
              <span className="brand-accent-bar" />
              Account · Optional
            </div>
            <h1 className="display-h1 text-5xl sm:text-6xl text-zinc-50 uppercase">
              {isRegister ? "Create account." : "Sign in."}
            </h1>
            <p className="mt-4 text-zinc-400 max-w-md leading-relaxed">
              Accounts are optional — you can still check out as a guest. Sign up to auto-fill checkout,
              track your order history in one place, and lock exclusive promo codes to your account.
            </p>

            <ul className="mt-8 space-y-3 font-mono-tech text-sm text-zinc-300">
              <li className="flex items-start gap-3"><ShieldCheck className="w-4 h-4 mt-0.5 text-red-500 shrink-0" /> Auto-fill name + email at every checkout</li>
              <li className="flex items-start gap-3"><ShieldCheck className="w-4 h-4 mt-0.5 text-red-500 shrink-0" /> All your PDOB orders in one clean history view</li>
              <li className="flex items-start gap-3"><ShieldCheck className="w-4 h-4 mt-0.5 text-red-500 shrink-0" /> One-per-customer promo codes locked to your account</li>
              <li className="flex items-start gap-3"><ShieldCheck className="w-4 h-4 mt-0.5 text-red-500 shrink-0" /> Never shared. Never sold. Cancelable anytime.</li>
            </ul>

            <Link
              to="/shop"
              className="mt-8 inline-flex items-center gap-1 font-mono-tech text-xs uppercase tracking-widest text-zinc-400 hover:text-zinc-100 link-underline"
            >
              Continue as guest <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          <form
            onSubmit={submit}
            className="border-2 border-zinc-800 bg-zinc-950 p-6 sm:p-8 h-fit"
            data-testid={isRegister ? "register-form" : "login-form"}
          >
            <div className="grid grid-cols-2 mb-6 border-2 border-zinc-800" data-testid="auth-mode-toggle">
              <button
                type="button"
                onClick={() => setMode("login")}
                data-testid="auth-tab-login"
                className={`py-2.5 font-mono-tech text-xs uppercase tracking-widest transition-colors ${
                  mode === "login" ? "bg-zinc-100 text-zinc-950" : "bg-transparent text-zinc-400 hover:text-zinc-200"
                }`}
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => setMode("register")}
                data-testid="auth-tab-register"
                className={`py-2.5 font-mono-tech text-xs uppercase tracking-widest transition-colors ${
                  mode === "register" ? "bg-zinc-100 text-zinc-950" : "bg-transparent text-zinc-400 hover:text-zinc-200"
                }`}
              >
                Create account
              </button>
            </div>

            {isRegister && (
              <label className="block mb-4">
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-400">Full name *</span>
                <input
                  required
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="register-name-input"
                  autoComplete="name"
                  className="mt-1 w-full bg-zinc-900 border-2 border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2.5 font-mono-tech text-sm text-zinc-100"
                />
              </label>
            )}

            <label className="block mb-4">
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-400">Email *</span>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid={isRegister ? "register-email-input" : "login-email-input"}
                autoComplete="email"
                className="mt-1 w-full bg-zinc-900 border-2 border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2.5 font-mono-tech text-sm text-zinc-100"
              />
            </label>

            <label className="block mb-6">
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-400">
                Password * {isRegister && <span className="text-zinc-600 normal-case">(min 8 characters)</span>}
              </span>
              <input
                required
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                data-testid={isRegister ? "register-password-input" : "login-password-input"}
                autoComplete={isRegister ? "new-password" : "current-password"}
                minLength={isRegister ? 8 : undefined}
                className="mt-1 w-full bg-zinc-900 border-2 border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2.5 font-mono-tech text-sm text-zinc-100"
              />
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid={isRegister ? "register-submit-btn" : "login-submit-btn"}
              className="w-full btn-brand disabled:opacity-50 px-6 py-3 font-display text-base tracking-widest uppercase"
            >
              {submitting ? "…" : isRegister ? "Create Account" : "Sign In"}
            </button>

            <div className="mt-4 font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest text-center">
              {isRegister ? (
                <>Already have an account?{" "}
                  <button type="button" onClick={() => setMode("login")} className="text-red-400 hover:text-red-300 link-underline">Sign in →</button>
                </>
              ) : (
                <>New here?{" "}
                  <button type="button" onClick={() => setMode("register")} className="text-red-400 hover:text-red-300 link-underline">Create account →</button>
                </>
              )}
            </div>
          </form>
        </div>
        <Footer />
      </div>
    );
  }

  // Signed-in state — profile + orders
  return (
    <div className="min-h-screen bg-zinc-950">
      <NavBar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="account-page">
        <div className="border-b-2 border-zinc-800 pb-6 mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center font-display text-2xl text-zinc-50" data-testid="account-avatar">
              {customer.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Account</div>
              <h1 className="display-h1 text-4xl sm:text-5xl text-zinc-50 uppercase mt-1" data-testid="account-name">{customer.name}</h1>
              <div className="mt-1 font-mono-tech text-xs text-zinc-400" data-testid="account-email">{customer.email}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="logout-btn"
            className="self-start sm:self-auto font-mono-tech text-xs uppercase tracking-widest text-zinc-400 hover:text-red-500 flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>

        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="display-h2 text-2xl sm:text-3xl text-zinc-50 uppercase flex items-center gap-3">
              <Package className="w-5 h-5" /> Order history
            </h2>
            {orders.length > 0 && (
              <span className="font-mono-tech text-xs uppercase text-zinc-500">{orders.length} {orders.length === 1 ? "order" : "orders"}</span>
            )}
          </div>

          {ordersLoading ? (
            <div className="font-mono-tech text-xs uppercase text-zinc-500">// Loading…</div>
          ) : orders.length === 0 ? (
            <div className="border-2 border-zinc-800 p-10 text-center" data-testid="account-orders-empty">
              <div className="font-mono-tech text-xs uppercase text-zinc-500">// You haven&apos;t placed any orders yet.</div>
              <Link to="/shop" className="mt-4 inline-flex items-center gap-1 font-mono-tech text-xs uppercase tracking-widest text-zinc-300 hover:text-zinc-100 link-underline">
                Browse the shop <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="account-orders-list">
              {orders.map((o) => (
                <Link
                  key={o.id}
                  to={`/track?order=${o.order_number}&email=${encodeURIComponent(o.customer_email)}`}
                  data-testid={`order-row-${o.order_number}`}
                  className="border-2 border-zinc-800 hover:border-zinc-300 bg-zinc-950 p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl text-zinc-50 tracking-wider">#{o.order_number}</div>
                    <div className="font-mono-tech text-[10px] text-zinc-500 uppercase mt-1">
                      {new Date(o.created_at).toLocaleDateString()} · {o.items.length} {o.items.length === 1 ? "item" : "items"}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`font-mono-tech text-[10px] px-2 py-0.5 uppercase tracking-widest ${
                      o.fulfillment_status === "shipped"
                        ? "bg-green-900/40 text-green-400"
                        : o.payment_status === "paid"
                        ? "bg-zinc-800 text-zinc-300"
                        : "bg-red-900/40 text-red-400"
                    }`}>
                      {o.fulfillment_status === "shipped" ? "Shipped" : o.payment_status === "paid" ? "Paid" : "Pending"}
                    </span>
                    <div className="font-mono-tech text-sm text-zinc-100 whitespace-nowrap">${o.total.toFixed(2)}</div>
                    <ArrowRight className="w-4 h-4 text-zinc-500" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default AccountPage;
